import { gql } from '@urql/core';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
    Espin_Event_Ticket,
    Espin_Event_Ticket_Type_Enum,
    Espin_Game_Definition_Enum,
    EventTicketsFragment,
    TicketRegistrationsFragment,
} from '../../../generated/spin-graphql';
import { PlainRouterQuery } from '../../../types/react.types';
import { LoadingIcon } from '../../svg/LoadingIcon';
import { EventTicketCard } from '../tickets/EventTicketCard';
import { useGetTeamsCount } from '../useGetTeamsCount';

type Props = {
    tickets: EventTicketsFragment[];
    ticketRegistrations: TicketRegistrationsFragment;
    withinRegistrationRange: boolean;
};

gql`
    fragment EventTickets on espin_event_ticket {
        id
        ...EventTicketCard
    }

    fragment TicketRegistrations on espin_event {
        event_tickets {
            id
            event_registrations {
                id
            }
        }
    }
`;

export const EventTickets: React.FC<Props> = ({ tickets, ticketRegistrations, withinRegistrationRange }) => {
    const router = useRouter();
    const { eventSlug } = router.query as PlainRouterQuery;
    const {
        control,
        getValues,
        formState: { isDirty },
        watch,
    } = useForm<{ selectedTicket: string }>();
    // we're not _actually_ submitting via a form, so use a state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const registerEvent = useCallback(() => {
        const vals = getValues();
        setIsSubmitting(true);
        router.push(`/events/${eventSlug}/register/${vals.selectedTicket}`);
    }, [eventSlug, getValues, router]);

    const [currentGame, setCurrentGame] = useState<Espin_Game_Definition_Enum | null>(null);
    const { teamsCount } = useGetTeamsCount({ game: currentGame });
    const selectedTicket = watch('selectedTicket');
    useEffect(() => {
        const ticket = tickets.find((t) => t.id === selectedTicket);
        if (ticket) {
            setCurrentGame(ticket.game.game_definition);
        }
    }, [selectedTicket, tickets]);

    const canRegisterWithTeam = useMemo(() => {
        const ticket = tickets.find((t) => t.id === selectedTicket);
        if (ticket?.ticket_type !== Espin_Event_Ticket_Type_Enum.Team) {
            return true;
        }
        return teamsCount > 0;
    }, [teamsCount, selectedTicket, tickets]);

    return (
        <div className="tickets">
            <Controller
                name={'selectedTicket'}
                control={control}
                rules={{}}
                defaultValue={null}
                render={({ field }) => {
                    return (
                        <>
                            {tickets.map((ticket: Espin_Event_Ticket, index: number) => (
                                <EventTicketCard
                                    // only allowing 1 registration per ticket, so accessing index 0 should be fine here?
                                    registrationId={
                                        ticketRegistrations?.event_tickets?.filter((t) => t.id === ticket.id)[0]
                                            ?.event_registrations[0]?.id
                                    }
                                    selected={field.value === ticket.id}
                                    key={`ticket-${index}`}
                                    ticket={ticket}
                                    onClick={() => field.onChange(ticket.id)}
                                />
                            ))}
                        </>
                    );
                }}
            />
            <button
                className="button large-button blue-button"
                disabled={!isDirty || isSubmitting || !withinRegistrationRange || !canRegisterWithTeam}
                onClick={(e) => {
                    e.preventDefault();
                    registerEvent();
                }}
            >
                {/* generic cannot register here... */}
                {isSubmitting && <LoadingIcon className="loading-icon rotating" />}
                {!isSubmitting && withinRegistrationRange && canRegisterWithTeam && 'Register'}
                {!withinRegistrationRange && 'Registration Closed'}
                {!canRegisterWithTeam && 'You must be a team leader'}
            </button>
        </div>
    );
};
